import React, { useEffect, useState, useRef } from 'react';
import withAuth from '../Utils/withAuth';
import {
  Flex, Box, Input, Button, HStack, Table, Thead, Tbody, Tr, Th, Td,
  Container, InputGroup, InputLeftElement, Spinner, useToast, Text, Link
} from '@chakra-ui/react';
import { Image } from '@chakra-ui/react';
import 'react-datepicker/dist/react-datepicker.css';
import shareicon from '../Images/Icons/share.svg';
import notshareicon from '../Images/Icons/notshare.svg';
import searchicon from '../Images/icon/search.svg';
import Footer from '../Components/General/Footer';
import ContentHeader from '../Components/General/ContentHeader';
import { getShareRecord, enableShareLinkFromRecord, cancelShareLinkFromRecord, getTransactionInfo } from '../Utils/api';
import BlockchainViewerModal from '../Components/Cert/BlockchainViewerModal.js';
import '../Styles/theme/blockchain_window.css';

// 在 ShareManagement 組件外部或文件頂部定義
const ThStyled = ({ children }) => (
  <Th
    fontFamily="Open Sans"
    fontWeight="600"
    fontSize="14px"
    lineHeight="150%"
    letterSpacing="1%"
    color="#00292B"
    verticalAlign="middle"
    whiteSpace="nowrap"
  >
    {children}
  </Th>
);

function ShareManagement() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterTrigger, setDateFilterTrigger] = useState(0);

  // 區塊鏈瀏覽器相關狀態
  const [showModel3, setShowModel3] = useState(false);
  const [tx_place_no, setTxPlaceNo] = useState('');
  const [tx_company_no, setTxCompanyNo] = useState('');
  const [tx_paction_no, setTxPactionNo] = useState('');
  const [tx_TransactionHash, setTxHash] = useState('');
  const [tx_block_hash, setTxBlockHash] = useState('');
  const [tx_block_number, setTxBlockNumber] = useState('');
  const [tx_raw_data, setTxRawData] = useState('');
  const [tx_event_type, setTxEventType] = useState('');
  const [tx_share_id, setTxShareId] = useState(null);
  const [tx_open_id, setTxOpenId] = useState(null);
  const [tx_pdf_hash, setTxPdfHash] = useState('');

  const handleLinkClick = (transaction_hash, log_index) => {
    getTransactionInfo(transaction_hash, log_index)
      .then((response_data) => {
        setTxPlaceNo(response_data.place_no);
        setTxCompanyNo(response_data.company_no);
        setTxPactionNo(response_data.paction_no);
        setTxBlockHash(response_data.block_hash);
        setTxBlockNumber(response_data.block_number);
        setTxRawData(response_data.raw_data);
        setTxEventType(response_data.event_type || '');
        setTxShareId(response_data.share_id !== undefined ? response_data.share_id : null);
        setTxOpenId(response_data.open_id !== undefined ? response_data.open_id : null);
        setTxPdfHash(response_data.pdf_hash || '');
        setTxHash(transaction_hash);
        setShowModel3(true);
      })
      .catch((err) => {
        console.error("Failed to get transaction:", err);
        toast({ title: '取得鏈上資訊失敗', status: 'error', duration: 2000 });
      });
  };

  const toast = useToast();
  const activeStartDate = useRef('');
  const activeEndDate = useRef('');
  const placeNo = (sessionStorage.getItem('place_no') || 'PLACE_001').replace(/"/g, '');
  const companyNo = (sessionStorage.getItem('company_no') || 'COMP_001').replace(/"/g, '');

  const reloadData = () => {
    setLoading(true);
    getShareRecord(placeNo, companyNo)
      .then(setRecords)
      .catch((err) => {
        console.error('取得分享記錄失敗', err);
        toast({
          title: '載入失敗',
          description: '無法取得分享記錄',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadData();
  }, []);

  useEffect(() => {
    let result = [];
    if (filter === 'all') {
      result = [...records];
    } else if (filter === 'enabled') {
      result = records.filter(item => item.share_enable);
    } else if (filter === 'disabled') {
      result = records.filter(item => !item.share_enable);
    } else if (filter === 'opened') {
      result = records.filter(item => item.open_records && item.open_records.length > 0);
    } else if (filter === 'unopened') {
      result = records.filter(item => !item.open_records || item.open_records.length === 0);
    }

    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(item =>
        (item.receiver_name && item.receiver_name.toLowerCase().includes(lowercasedTerm)) ||
        (item.company_name && item.company_name.toLowerCase().includes(lowercasedTerm)) ||
        (item.paction_no && item.paction_no.toLowerCase().includes(lowercasedTerm)) ||
        (item.receiver_email && item.receiver_email.toLowerCase().includes(lowercasedTerm)) ||
        (item.receiver_company_no && item.receiver_company_no.toLowerCase().includes(lowercasedTerm)) ||
        (item.receiver_place_no && item.receiver_place_no.toLowerCase().includes(lowercasedTerm)) ||
        (item.receiver_place_name && item.receiver_place_name.toLowerCase().includes(lowercasedTerm))
      );
    }

    const formatDate = (str) => {
      try {
        return new Date(str.replace(/[\u4e00-\u9fa5]/g, '/').replace(/\//g, '-'));
      } catch (e) {
        console.error('日期格式錯誤:', str, e);
        return new Date(0); // 返回一個極早的日期作為備用
      }
    };

    // 使用 useRef 中存儲的活動篩選日期
    if (activeStartDate.current || activeEndDate.current) {
      if (activeStartDate.current && activeEndDate.current &&
        new Date(activeStartDate.current) > new Date(activeEndDate.current)) {
        toast({
          title: '日期錯誤',
          description: '起始日期不能晚於結束日期',
          status: 'warning',
          duration: 3000
        });
      } else {
        if (activeStartDate.current) {
          const start = new Date(activeStartDate.current);
          result = result.filter(item => {
            const itemDate = formatDate(item.share_time);
            return itemDate && itemDate >= start;
          });
        }
        if (activeEndDate.current) {
          const end = new Date(activeEndDate.current);
          end.setHours(23, 59, 59, 999);
          result = result.filter(item => {
            const itemDate = formatDate(item.share_time);
            return itemDate && itemDate <= end;
          });
        }
      }
    }

    result.sort((a, b) => {
      const dateA = formatDate(a.share_time);
      const dateB = formatDate(b.share_time);
      return dateB - dateA;
    });

    setFilteredRecords(result);
  }, [records, filter, searchTerm, dateFilterTrigger]);

  const handleFilter = (filterType) => {
    if (filter === filterType) {
      setFilter('all');
    } else {
      setFilter(filterType);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEnable = (id) => {
    enableShareLinkFromRecord(id)
      .then(() => {
        toast({ title: '已啟用分享', status: 'success', duration: 2000 });
        reloadData();
      })
      .catch(() => {
        toast({ title: '啟用失敗', status: 'error', duration: 2000 });
      });
  };

  const handleCancel = (id) => {
    cancelShareLinkFromRecord(id)
      .then(() => {
        toast({ title: '已取消分享', status: 'success', duration: 2000 });
        reloadData();
      })
      .catch(() => {
        toast({ title: '取消失敗', status: 'error', duration: 2000 });
      });
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    activeStartDate.current = '';
    activeEndDate.current = '';
    setDateFilterTrigger(prev => prev + 1);
    toast({
      title: '已清除日期篩選',
      status: 'info',
      duration: 2000
    });
  };

  const handleApplyDateFilter = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast({
        title: '日期錯誤',
        description: '起始日期不能晚於結束日期',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    activeStartDate.current = startDate;
    activeEndDate.current = endDate;
    setDateFilterTrigger(prev => prev + 1);

    if (startDate || endDate) {
      toast({
        title: '已應用日期篩選',
        status: 'success',
        duration: 2000
      });
    }
  };

  const exportToCSV = () => {
    try {
      const headers = [
        '分享時間',
        '狀態',
        '許可證號',
        '公司名稱',
        '收件者',
        '信箱'
      ].join(',');

      // 使用 filteredRecords 而不是 records
      const csvRows = filteredRecords.map(item => {
        return [
          `"${item.share_time || ''}"`,
          `"${item.share_enable ? '啟用中' : '關閉'}"`,
          `"${item.paction_no || ''}"`,
          `"${item.company_name || ''}"`,
          `"${item.receiver_name || ''}"`,
          `"${item.receiver_email || ''}"`,
        ].join(',');
      });

      const csvContent = [headers, ...csvRows].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // 添加篩選資訊到檔名
      const now = new Date();
      let filenameParts = ['分享記錄'];

      const fileName = `${filenameParts.join('_')}_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.csv`;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: `匯出成功 (${filteredRecords.length} 筆記錄)`,
        status: 'success',
        duration: 2000,
      });
    }
    catch (error) {
      console.error('匯出CSV失敗', error);
      toast({
        title: '匯出失敗',
        description: '無法匯出CSV檔案',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Flex direction="column" w="100vw" minH="100vh" bg="#F4F6F7">
      <Box flex="1">
        <ContentHeader
          tabs={[
            { label: '我的證書', path: '/home' },
            { label: '分享管理', path: '/licenseshare' },
            { label: '接收紀錄', path: '/recipientshare' },
          ]}
        />

        <Container maxW="1440px" mt="40px" px={0}>
          <Flex justify="space-between" align="center" flexWrap="wrap">
            <HStack spacing="18px" flexGrow={1} alignItems="center">
              <InputGroup w="304px" h="40px">
                <InputLeftElement pointerEvents="none" height="100%">
                  <Image src={searchicon} boxSize="16px" alt="搜尋" />
                </InputLeftElement>
                <Input
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="收件者、公司、許可證、信箱、管編"
                  bg="white"
                  border="1px solid #A8B8C2"
                  borderRadius="3px"
                  fontSize="14px"
                  fontWeight="400"
                  color="#4F6470"
                  h="40px"
                  pl="32px"
                  _placeholder={{ color: '#4F6470' }}
                />
              </InputGroup>

              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                bg="white"
                border="1px solid #A8B8C2"
                borderRadius="3px"
                fontSize="14px"
                fontWeight="400"
                color="#4F6470"
                h="40px"
                w="160px"
                zIndex="1"
              />
              <Text color="#4F6470">~</Text>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                bg="white"
                border="1px solid #A8B8C2"
                borderRadius="3px"
                fontSize="14px"
                fontWeight="400"
                color="#4F6470"
                h="40px"
                w="160px"
                zIndex="1"
              />
              <Button
                size="sm"
                onClick={handleApplyDateFilter}
                colorScheme="teal"
                zIndex="1"
              >
                確定
              </Button>
              <Button
                size="sm"
                onClick={handleClearDates}
                zIndex="1"
              >
                清除
              </Button>

              <Button
                bg={filter === 'enabled' ? "#B2F5B2" : "#DAE0E5"}
                color="#00292B"
                borderRadius="3px"
                w="73px"
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={() => handleFilter('enabled')}
              >
                啟用分享
              </Button>
              <Button
                bg={filter === 'disabled' ? "#B2F5B2" : "#DAE0E5"}
                color="#00292B"
                borderRadius="3px"
                w="73px"
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={() => handleFilter('disabled')}
              >
                關閉分享
              </Button>
              <Button
                bg={filter === 'opened' ? "#B2F5B2" : "#DAE0E5"}
                color="#00292B"
                borderRadius="3px"
                w="73px"
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={() => handleFilter('opened')}
              >
                已開啟
              </Button>
              <Button
                bg={filter === 'unopened' ? "#B2F5B2" : "#DAE0E5"}
                color="#00292B"
                borderRadius="3px"
                w="73px"
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={() => handleFilter('unopened')}
              >
                未開啟
              </Button>
            </HStack>
            <HStack spacing={2}>
              <Button
                bg="#DAE0E5"
                color="#00292B"
                borderRadius="3px"
                px={4}
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={exportToCSV}
              >
                匯出
              </Button>
            </HStack>
          </Flex>
        </Container>

        <Container maxW="1440px" mt="16px" mb={10} px={0}>
          <Box overflowX="auto" bg="white" borderRadius="md">
            {loading ? (
              <Flex justify="center" align="center" p={6}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <Table>
                <Thead bg="#FFFFFF">
                  <Tr>
                    <ThStyled>分享時間</ThStyled>
                    <ThStyled>狀態</ThStyled>
                    <ThStyled>快速設定分享</ThStyled>
                    <ThStyled>許可證號</ThStyled>
                    <ThStyled>公司名稱</ThStyled>
                    <ThStyled>收件者</ThStyled>
                    <ThStyled>信箱</ThStyled>
                    <ThStyled>運作人</ThStyled>
                    <ThStyled>運作場所</ThStyled>
                    <ThStyled>查閱狀態</ThStyled>
                    <ThStyled>分享 TX</ThStyled>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredRecords.length === 0 ? (
                    <Tr>
                      <Td colSpan={12} textAlign="center" py={6}>
                        無符合條件的資料
                      </Td>
                    </Tr>
                  ) : (
                    filteredRecords.map((item) => {
                      const hasOpened = item.open_records && item.open_records.length > 0;
                      const latestOpenTime = hasOpened ? item.open_records[0].open_time : null;
                      return (
                        <Tr key={item.id}>
                          <Td whiteSpace="nowrap">{item.share_time}</Td>
                          <Td whiteSpace="nowrap">{item.share_enable ? '啟用中' : '關閉'}</Td>
                          <Td>
                            {item.share_enable ? (
                              <Button
                                size="sm"
                                onClick={() => handleCancel(item.id)}
                                leftIcon={
                                  <Image
                                    src={notshareicon}
                                    boxSize="24px"
                                    ml="-2px"
                                  />
                                }
                                variant="unstyled"
                                display="flex"
                                alignItems="center"
                                color="#00292B"
                                fontFamily="Open Sans"
                                fontWeight="400"
                                fontSize="14px"
                                lineHeight="150%"
                                letterSpacing="1%"
                                _hover={{ color: "blue.500" }}
                              >
                                關閉分享
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleEnable(item.id)}
                                leftIcon={
                                  <Image
                                    src={shareicon}
                                    boxSize="24px"
                                    ml="-2px"
                                  />
                                }
                                variant="unstyled"
                                display="flex"
                                alignItems="center"
                                color="#00292B"
                                fontFamily="Open Sans"
                                fontWeight="400"
                                fontSize="14px"
                                lineHeight="150%"
                                letterSpacing="1%"
                                _hover={{ color: "blue.500" }}
                              >
                                啟用分享
                              </Button>
                            )}
                          </Td>
                          <Td whiteSpace="nowrap">{item.paction_no}</Td>
                          <Td whiteSpace="nowrap">{item.company_name}</Td>
                          <Td whiteSpace="nowrap">{item.receiver_name}</Td>
                          <Td>{item.receiver_email}</Td>
                          <Td>
                            <Flex direction="column">
                              <Text>{item.receiver_company}</Text>
                              {item.receiver_company_no && <Text fontSize="sm" color="gray.500">{item.receiver_company_no}</Text>}
                            </Flex>
                          </Td>
                          <Td>
                            <Flex direction="column">
                              <Text>{item.receiver_place_name || ''}</Text>
                              {item.receiver_place_no && <Text fontSize="sm" color="gray.500">{item.receiver_place_no}</Text>}
                            </Flex>
                          </Td>
                          <Td>
                            <Flex direction="column" align="flex-start">
                              <Box
                                px={2}
                                py={1}
                                borderRadius="md"
                                bg={hasOpened ? "green.50" : "gray.50"}
                                color={hasOpened ? "green.600" : "gray.600"}
                                display="inline-block"
                                fontSize="sm"
                                fontWeight="bold"
                              >
                                {hasOpened ? '已開啟' : '未開啟'}
                              </Box>
                              {hasOpened && latestOpenTime && (
                                <Text fontSize="xs" color="gray.400" mt={1}>
                                  {latestOpenTime}
                                </Text>
                              )}
                            </Flex>
                          </Td>
                          <Td whiteSpace="nowrap">
                            {item.tx_hash ? (
                              <Link
                                color="teal.500"
                                textDecoration="underline"
                                onClick={() => handleLinkClick(item.tx_hash)}
                                style={{ cursor: 'pointer' }}
                              >
                                {item.tx_hash.substring(0, 10)}...
                              </Link>
                            ) : '無'}
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            )}
          </Box>
        </Container>
      </Box>

      {showModel3 && (
        <BlockchainViewerModal
          visible={showModel3}
          tx_block_number={tx_block_number}
          tx_paction_no={tx_paction_no}
          tx_place_no={tx_place_no}
          tx_company_no={tx_company_no}
          tx_block_hash={tx_block_hash}
          tx_TransactionHash={tx_TransactionHash}
          tx_raw_data={tx_raw_data}
          tx_event_type={tx_event_type}
          tx_share_id={tx_share_id}
          tx_open_id={tx_open_id}
          tx_pdf_hash={tx_pdf_hash}
          onClose={() => setShowModel3(false)}
        />
      )}

      <Footer />
    </Flex>
  );
}

export default withAuth(ShareManagement);